"use client";

import { useLocale, useTranslations } from "next-intl";
import { useRouter, usePathname } from "@/i18n/navigation";
import { cn } from "@/lib/utils";

interface LanguageSwitcherProps {
  className?: string;
}

/**
 * Pill-style EN / عربي toggle. Works in any route context because it uses
 * next-intl's locale-aware router and pathname hooks.
 */
export function LanguageSwitcher({ className }: LanguageSwitcherProps) {
  const locale = useLocale();
  const t = useTranslations("appNav");
  const router = useRouter();
  const pathname = usePathname();

  function switchTo(next: "en" | "ar") {
    router.replace(pathname, { locale: next });
  }

  return (
    <div
      className={cn(
        "flex items-center gap-0.5 rounded-lg border border-border bg-muted p-0.5",
        className,
      )}
      role="group"
      aria-label={t("languageSwitcher")}
    >
      <button
        onClick={() => switchTo("en")}
        aria-pressed={locale === "en"}
        className={cn(
          "px-2.5 py-1 rounded-md text-xs font-semibold transition-all duration-150 tracking-wide",
          locale === "en"
            ? "bg-background text-foreground shadow-sm"
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        EN
      </button>
      <button
        onClick={() => switchTo("ar")}
        aria-pressed={locale === "ar"}
        className={cn(
          "px-2.5 py-1 rounded-md text-xs font-medium transition-all duration-150",
          locale === "ar"
            ? "bg-background text-foreground shadow-sm"
            : "text-muted-foreground hover:text-foreground",
        )}
        style={{ fontFamily: "'IBM Plex Sans Arabic', 'Inter', sans-serif" }}
      >
        عربي
      </button>
    </div>
  );
}
